import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import styles from './BuyCreditsPage.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import {
  GET_CALCULATOR_PAY_ACCESS,
  GET_CUSTOMER_ACCOUNT_PAYMENT_METHODS,
  GET_DRAFT_PRICE,
  MAKE_CALCULATOR_PAYMENT,
} from '../../api';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ReactComponent as Stripe } from '../../assets/svg/stripe.svg';
import { Alert, Snackbar } from '@mui/material';
import { Button, Modal } from 'tt-ui-lib/core';
import { AddIcon } from 'tt-ui-lib/icons';
import DisclaimerAgreement from './DisclaimerAgreement';
import { CALC_NAMES } from 'utils/calculatorUtils';
import { URL_CALCS_DRAFTS, URL_CALCS_SCORE } from 'constants/calculatorsPage';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import AddNewCardModal from '../../core/Profile/CardsAndWallets/Cards/AddNewCardModal';

const BuyCreditsPage = () => {
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [isDisabledPaymentButton, setIsDisabledPaymentButton] = useState(false);
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [cost, setCost] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [cardsList, setCardsList] = useState([]);
  const [openAddNewCardModal, setOpenAddNewCardModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState('');
  const [canPayDetails, setCanPayDetails] = useState(null);
  const currentHost = `${window.location.protocol}//${window.location.host}`;
  const [getDraftPrice, { data: priceByDraftId, error: errorPriceByDraftId }] = useLazyQuery(
    GET_DRAFT_PRICE,
    { variables: { input: { draft_id: draftId } } }
  );

  const [getCalculatorPayAccess] = useLazyQuery(GET_CALCULATOR_PAY_ACCESS, {
    variables: { input: draftId },
  });

  const [checkoutLinkMutation, { data: calculatorServiceData, loading, error }] =
    useMutation(MAKE_CALCULATOR_PAYMENT);
  const [getCustomerAccountPaymentMethods] = useLazyQuery(GET_CUSTOMER_ACCOUNT_PAYMENT_METHODS, {
    fetchPolicy: 'network-only',
  });

  const openModalChange = (value) => {
    setIsModalOpen(value);
  };

  const fetchCardsListData = async () => {
    const res = await getCustomerAccountPaymentMethods({});
    if (res.data && res.data.getCustomerAccountPaymentMethods) {
      setCardsList(res.data.getCustomerAccountPaymentMethods);
    }
  };

  const fetchCalculatorPayAccessData = async () => {
    const res = await getCalculatorPayAccess({});
    if (res.data && res.data.getCalculatorPayAccess) {
      setCanPayDetails(res.data.getCalculatorPayAccess);
    }
  };

  const onPayClick = () => {
    setIsDisabledPaymentButton(true);
    setOpenErrorAlert(false);
    checkoutLinkMutation({
      variables: {
        input: {
          exact_id: draftId,
          payment_system: `stripe`,
          card_id: selectedCard,
        },
      },
    });
  };

  const onBackClick = () => {
    navigate(`${URL_CALCS_DRAFTS}`);
  };

  const confirmChange = () => {
    setIsModalOpen(false);
    setIsConfirm(true);
  };

  useEffect(() => {
    (async () => {
      await getDraftPrice();
      await fetchCardsListData();
      await fetchCalculatorPayAccessData();
    })();
    return () => {
      setIsConfirm(false);
      setIsModalOpen(false);
    };
  }, []);

  useEffect(() => {
    if (calculatorServiceData?.payForCalculatorDraft?.payment_status) {
      window.location.href = `${currentHost}${URL_CALCS_SCORE}`;
    }
  }, [calculatorServiceData]);

  useEffect(() => {
    if (cardsList.length > 0) {
      const defaultCard = cardsList.find((card) => card.is_default);
      if (defaultCard && defaultCard.id) {
        setSelectedCard(defaultCard.id);
      } else {
        setSelectedCard(cardsList[0].id);
      }
    }
  }, [cardsList]);

  useEffect(() => {
    if (priceByDraftId && priceByDraftId.getCalculatorPrice) {
      setCost(priceByDraftId.getCalculatorPrice);
    }
  }, [priceByDraftId]);

  useEffect(() => {
    if (!cost) return;
    setIsModalOpen(true);
  }, [cost]);

  useEffect(() => {
    if (errorPriceByDraftId) {
      setOpenErrorAlert(true);
      setIsDisabledPaymentButton(false);
      console.error(errorPriceByDraftId);
    }
  }, [errorPriceByDraftId]);

  useEffect(() => {
    if (error) {
      setOpenErrorAlert(true);
      setIsDisabledPaymentButton(false);
      console.error(error);
    }
  }, [error]);

  return (
    <>
      {cost && canPayDetails?.canPay && (
        <>
          <div className={styles.contentBox}>
            <h1>Payment</h1>
            <div className={styles.info}>
              <div>
                Service:
                <span>{CALC_NAMES[cost.calcType]}</span>
              </div>
              <div>
                Amount:
                <span>${cost.finalCost}</span>
              </div>
              <div>Payment methods:</div>
              <div>
                <div
                  id="stripe"
                  className={clsx(styles.payMethod, selected && styles.selected)}
                  onClick={() => setSelected((value) => (value ? null : 'stripe'))}
                >
                  <Stripe />
                  Stripe
                </div>
              </div>
              {selected === 'stripe' && (
                <>
                  {cardsList.length > 0 ? (
                    <div className={styles.cardsBlock}>
                      <Select
                        defaultValue={false}
                        className={styles.select}
                        onChange={(e) => setSelectedCard(e.target.value)}
                        value={selectedCard}
                        style={{ display: 'flex' }}
                        sx={{
                          '.MuiOutlinedInput-notchedOutline': {
                            border: '0 !important',
                          },
                        }}
                        renderValue={(value) =>
                          value ? (
                            <div style={{ display: 'flex' }}>
                              <div>
                                {cardsList.find((item) => item.id === value) &&
                                  `**** **** **** ${cardsList.find((item) => item.id === value).last4}`}
                              </div>
                            </div>
                          ) : (
                            <div>Select card to pay</div>
                          )
                        }
                      >
                        {cardsList.map((item, index) => (
                          <MenuItem key={item.id} value={item.id}>
                            <div>**** **** **** {item.last4}</div>
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        type="link"
                        className={styles.addCardBtn}
                        onClick={() => setOpenAddNewCardModal(true)}
                      >
                        <AddIcon style={{ width: 24, height: 24 }} />
                        Add new card
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      className={styles.addCardBtn}
                      style={{ marginTop: '12px' }}
                      onClick={() => setOpenAddNewCardModal(true)}
                    >
                      <AddIcon style={{ width: 24, height: 24 }} />
                      Add new card
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.buttonCont}>
            <Button type="default" onClick={onBackClick}>
              Back
            </Button>
            <Button
              type="primary"
              onClick={isConfirm ? () => onPayClick() : () => openModalChange(true)}
              disabled={!selected || isDisabledPaymentButton || !cardsList.length || !selectedCard}
            >
              Pay
            </Button>
          </div>
        </>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={openErrorAlert}
        onClose={() => setOpenErrorAlert(false)}
      >
        <Alert
          onClose={() => setOpenErrorAlert(false)}
          severity="error"
          sx={{ width: '100%', backgroundColor: '#D32F2F', color: '#ffffff' }}
        >
          Something went wrong, try again
        </Alert>
      </Snackbar>
      {cost && (
        <DisclaimerAgreement
          cost={cost}
          isModalOpen={isModalOpen}
          setIsModalOpen={openModalChange}
          confirmChange={confirmChange}
        />
      )}
      {setOpenAddNewCardModal && (
        <AddNewCardModal
          open={openAddNewCardModal}
          setOpen={setOpenAddNewCardModal}
          setCardsList={setCardsList}
        />
      )}
      {canPayDetails && !canPayDetails?.canPay && (
        <Modal
          open={!canPayDetails?.canPay}
          onClose={() => navigate(`${URL_CALCS_DRAFTS}`)}
          onClick={(e) => e.stopPropagation()}
          title="Payment is not available"
          className={styles.startModal}
        >
          {!canPayDetails?.paymentData?.status === 'success' ? (
            <div>
              This calculator is already paid for. You will be redirected to the drafts page
            </div>
          ) : (
            <div>
              You don&apos;t have access to pay for this calculator. Please contact support. You
              will be redirected to the drafts page
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default BuyCreditsPage;
